import { formatShortDateAndHour } from './formatDatesAndHours';
import { ISubStep } from '../components/VerticalStepper/VerticalStepper';
import { IEventType, IOrderHistory, ILogisticServiceHistory } from '../types/suscription/history.types';
import { DTOgetOrderDetailedData, IorderDetailsDates, ORDER_STATUS } from '../types/orders';
import { IHistorySL, IlogisticServiceDetails, LOGISTIC_SERVICE_STATUS_NAME } from '../types/services';
import {
  determineNewOrderStatus,
  LOGISTIC_SERVICE_HISTORY_BASE_EVENTS,
  LOGISTIC_SERVICE_STATUS_SEQUENCE,
  ORDER_HISTORY_BASE_EVENTS,
  ORDER_STATUS_SEQUENCE,
  TLogisticServiceDates,
  TOrderDates
} from '../constants/history.constants';
import { IRelevantInfo } from 'types/suscription/history.types';
import { modifyEventContent } from './history-events';

/**
 * Actualiza los nombres de los estados del historial de la orden
 * @param orderHistory - Historial de la orden
 * @returns Historial de la orden con los nombres de estados actualizados
 */
export const updateCalculatedOrderHistoryStatus = (orderHistory: IOrderHistory[]) => {
  return orderHistory.map(item => ({
    ...item,
    name: determineNewOrderStatus(item.statusId) || item.name
  }));
};

export interface IBaseHistoryEvent {
  statusName?: string;
  eventType?: IEventType;
  createdAt?: string;
  relevantInfo?: IRelevantInfo;
}

export interface IBaseHistory {
  statusId?: number;
  name?: string;
  events: IBaseHistoryEvent[];
}

/**
 * Mapea un evento de historial a un subpaso del componente VerticalStepper
 * @param event - El evento de historial a mapear
 * @returns Objeto con el título, la descripción y la fecha del evento
 */
const mapEventToSubStep = <T extends IBaseHistoryEvent>(event: T): ISubStep => {
  return {
    title: event.eventType?.name || '',
    description: event.eventType?.description || '',
    date: formatShortDateAndHour(event.createdAt || '')?.textWithTimezone || ''
  };
};

/**
 * Mapea los eventos de historial a subpasos del componente VerticalStepper
 * @param events - Eventos de historial a mapear
 * @returns Array de objetos con el título, la fecha y la descripción de cada evento
 */
export const mapEventsToSubSteps = <T extends IBaseHistoryEvent>(events: T[]): ISubStep[] => {
  return events.map((event: T) => mapEventToSubStep(event));
};

export interface ILastEventToDisplay extends ISubStep {
  isProblemActive: boolean;
}

/**
 * Crea un objeto para renderizar en el componente LastUpdate
 * @param lastEvent - Evento más reciente del historial a renderizar
 * @param isProblemActive - Indica si hay un problema activo
 * @returns Objeto con el evento más reciente a renderizar
 */
export const createLastEventToDisplayFromHistory = <T extends IBaseHistoryEvent>(
  lastEvent?: T | null,
  isProblemActive = false
): ILastEventToDisplay | null => {
  if (!lastEvent) return null;

  return { ...mapEventToSubStep(lastEvent), isProblemActive };
};

/**
 * Encuentra el evento más reciente de todo el historial del servicio logístico o de la orden
 * @param history - Historial del servicio logístico o de la orden
 * @returns Evento más reciente
 */
export const getLastHistoryEvent = <T extends ILogisticServiceHistory | IOrderHistory>(
  history?: T[]
): IBaseHistoryEvent | null => {
  if (!history?.length) return null;
  let latestEvent: IBaseHistoryEvent | null = null;
  let latestTimestamp = 0;

  for (let i = history.length - 1; i >= 0; i--) {
    const status = history[i];
    if (!status.events?.length) continue;

    for (let j = status.events.length - 1; j >= 0; j--) {
      const event = status.events[j];
      const timestamp = event.createdAt ? new Date(event.createdAt).getTime() : 0;
      if (timestamp > latestTimestamp) {
        latestTimestamp = timestamp;
        latestEvent = event;
      }
    }
  }

  return latestEvent;
};

/**
 * Crea un evento base de historial
 * @param name - Nombre del evento
 * @param description - Descripción del evento
 * @returns Evento de historial
 */
export const createEvent = (name: string, description: string): IBaseHistoryEvent => {
  return { eventType: { name, description } as IEventType };
};

/**
 * Función auxiliar que crea un historial base de una orden a partir de fechas correspondientes a estados de la orden
 * @param dates - Fechas correspondientes a estados de la orden
 * @returns Historial base de la orden
 */
const createOrderHistoryFromDates = (dates: IorderDetailsDates): IBaseHistory[] => {
  const baseHistory: IBaseHistory[] = [];
  Object.entries(dates).forEach(([key, value]) => {
    if (!value) return;

    const baseEvent = ORDER_HISTORY_BASE_EVENTS[key as TOrderDates];
    if (!baseEvent) return;

    const events = [{ eventType: baseEvent.events[0].eventType, createdAt: value }];

    baseHistory.push({ name: baseEvent.statusName, events });
  });

  return baseHistory;
};

/**
 * Función auxiliar que crea un historial base de un servicio logístico a partir de fechas correspondientes a estados del servicio logístico
 * @param dates - Fechas correspondientes a estados del servicio logístico
 * @returns Historial base
 */
const createLogisticServiceHistoryFromDates = (dates: IHistorySL[]): IBaseHistory[] => {
  const baseHistory: IBaseHistory[] = [];

  dates.forEach((value: IHistorySL) => {
    const baseEvent = LOGISTIC_SERVICE_HISTORY_BASE_EVENTS[value.status as TLogisticServiceDates];
    if (!baseEvent) return;

    const events = [{ eventType: baseEvent.events[0].eventType, createdAt: value.date }];

    baseHistory.push({ name: baseEvent.statusName, events });
  });

  return baseHistory;
};

/**
 * Función auxiliar que determina la secuencia de estados según el estado actual e historial base
 * @param currentStatusName - Nombre del estado actual
 * @param baseHistory - Historial base
 * @param statusName - Objeto con los nombres de estados (T)
 * @param statusSequence - Array con la secuencia de estados (U)
 * @returns Array de estados en secuencia
 */
const determineStatusSequence = <T extends { CANCELLED: string }, U extends string[]>(
  currentStatusName: string,
  baseHistory: IBaseHistory[],
  statusName: T,
  statusSequence: U
): U => {
  // Si el estado actual no es 'cancelado', se retorna la secuencia de estados normal hasta el estado actual
  if (currentStatusName !== statusName.CANCELLED) {
    const currentStatusSequence = statusSequence.indexOf(currentStatusName);
    return statusSequence.slice(0, currentStatusSequence + 1) as U;
  }

  // Si el estado actual es 'cancelado', se retorna la secuencia de estados hasta el estado con el índice más alto añadiendo el estado 'cancelado' al final de la secuencia
  let maxSequenceIndex = -1;
  baseHistory.forEach(status => {
    const sequenceIndex = statusSequence.indexOf(status.name as string);
    if (sequenceIndex > maxSequenceIndex) {
      maxSequenceIndex = sequenceIndex;
    }
  });

  const resultSequence = statusSequence.slice(0, maxSequenceIndex + 1) as U;
  resultSequence.push(currentStatusName);
  return resultSequence;
};

/**
 * Función auxiliar que reordena los eventos del historial base
 * @param baseHistory - Historial base
 * @param sequence - Secuencia de estados (U)
 * @returns Historial base con eventos reordenados
 */
const reorderHistoryBySequence = <U extends string[]>(baseHistory: IBaseHistory[], sequence: U): IBaseHistory[] => {
  const attachedHistory = sequence.map(statusName => {
    const status = baseHistory.find(h => h.name === statusName);
    if (!status) return { name: statusName, events: [] };
    return status;
  });

  const reorderedHistory = attachedHistory
    .map((status, index) => {
      if (!status) return null;

      // El último estado (a menos que sea haya un solo) debe quedar sin eventos
      if (index > 0 && index === attachedHistory.length - 1) {
        return { ...status, events: [] };
      }

      const nextStatus = attachedHistory[index + 1];
      const hasNextStatusWithEvents = nextStatus && nextStatus.events && nextStatus.events.length > 0;

      // El primer estado debe conservar su evento original y agregar el del siguiente
      if (index === 0) {
        let events = [...status.events];
        if (hasNextStatusWithEvents) {
          events = [...events, ...nextStatus.events];
        }
        return { ...status, events };
      }

      // Los demás estados deben tener el evento del estado siguiente
      if (hasNextStatusWithEvents) {
        return { ...status, events: nextStatus.events };
      }

      // Si no hay estado siguiente o no tiene eventos, mantener el estado original
      return status;
    })
    .filter(Boolean);

  return reorderedHistory as IBaseHistory[];
};

/**
 * Función auxiliar que crea el historial base de una orden a partir de fechas correspondientes a estados de la orden
 * @param orderDates - Fechas de la orden
 * @param currentStatusName - Nombre del estado actual
 * @returns Historial base
 */
const createBaseOrderHistory = (orderDates: IorderDetailsDates, currentStatusName: ORDER_STATUS): IBaseHistory[] => {
  const historyFromDates = createOrderHistoryFromDates(orderDates);
  const statusSequence = determineStatusSequence(
    currentStatusName,
    historyFromDates,
    ORDER_STATUS,
    ORDER_STATUS_SEQUENCE
  );
  return reorderHistoryBySequence(historyFromDates, statusSequence);
};

/**
 * Función auxiliar que crea el historial base de un servicio logístico a partir de fechas correspondientes a estados del servicio logístico
 * @param logisticServiceDates - Fechas del servicio logístico
 * @param currentStatusName - Nombre del estado actual
 * @returns Historial base
 */
const createBaseLogisticServiceHistory = (
  logisticServiceDates: IHistorySL[],
  currentStatusName: string
): IBaseHistory[] => {
  const historyFromDates = createLogisticServiceHistoryFromDates(logisticServiceDates);
  const statusSequence = determineStatusSequence(
    currentStatusName,
    historyFromDates,
    LOGISTIC_SERVICE_STATUS_NAME,
    LOGISTIC_SERVICE_STATUS_SEQUENCE
  );
  return reorderHistoryBySequence(historyFromDates, statusSequence);
};

/**
 * Función auxiliar que combina el historial base con el historial de una orden
 * @param currentStatusName - Nombre del estado actual
 * @param baseHistory - Historial base
 * @param orderHistory - Historial de una orden
 * @returns Historial completo
 */
const combineBaseHistoryWithOrderHistory = (
  currentStatusName: string,
  baseHistory: IBaseHistory[],
  orderHistory: IOrderHistory[]
): IOrderHistory[] => {
  const statusSequence = determineStatusSequence(currentStatusName, baseHistory, ORDER_STATUS, ORDER_STATUS_SEQUENCE);

  const combinedHistory = statusSequence.map(statusName => {
    // Se prioriza el historial de una orden sobre el historial base
    const fetched = orderHistory.find(history => history.name === statusName);
    if (fetched && fetched.events && fetched.events.length > 0) {
      const modifiedEvents = fetched.events.map(event => {
        return { ...event, eventType: { ...event.eventType, ...modifyEventContent(event) } };
      });
      return { ...fetched, events: modifiedEvents };
    }

    const base = baseHistory.find(base => base.name === statusName);
    if (!base) return { statusId: statusName, events: [] };
    return base;
  });

  return combinedHistory as IOrderHistory[];
};

/**
 * Función auxiliar que combina el historial base con el historial de un servicio logístico
 * @param currentStatusName - Nombre del estado actual
 * @param baseHistory - Historial base
 * @param logisticServiceHistory - Historial de un servicio logístico
 * @returns Historial completo
 */
const combineBaseHistoryWithLogisticServiceHistory = (
  currentStatusName: string,
  baseHistory: IBaseHistory[],
  logisticServiceHistory: ILogisticServiceHistory[]
): ILogisticServiceHistory[] => {
  const statusSequence = determineStatusSequence(
    currentStatusName,
    baseHistory,
    LOGISTIC_SERVICE_STATUS_NAME,
    LOGISTIC_SERVICE_STATUS_SEQUENCE
  );

  const combinedHistory = statusSequence.map(statusName => {
    // Se prioriza el historial de un servicio logístico sobre el historial base
    const fetched = logisticServiceHistory.find(history => history.name === statusName);
    if (fetched && fetched.events && fetched.events.length > 0) {
      const modifiedEvents = fetched.events.map(event => {
        return { ...event, eventType: { ...event.eventType, ...modifyEventContent(event) } };
      });
      return { ...fetched, events: modifiedEvents };
    }

    const base = baseHistory.find(base => base.name === statusName);
    if (!base) return { statusId: statusName, events: [] };
    return base;
  });

  return combinedHistory as ILogisticServiceHistory[];
};

/**
 * Construye el historial completo de una orden a partir de los datos de la orden y el historial de la orden
 * @param orderDetailedData - Datos de la orden
 * @param orderHistory - Historial de la orden
 * @returns Historial completo
 */
export const buildCompleteOrderHistory = (
  orderDetailedData: DTOgetOrderDetailedData,
  orderHistory: IOrderHistory[]
): IOrderHistory[] => {
  const { orderDates, statusName } = orderDetailedData;
  if (!orderDates || !statusName) return [];

  const baseHistory = createBaseOrderHistory(orderDates, statusName as ORDER_STATUS);
  return combineBaseHistoryWithOrderHistory(statusName, baseHistory, orderHistory);
};

/**
 * Construye el historial completo de un servicio logístico a partir de los datos del servicio logístico y el historial del servicio logístico
 * @param logisticServiceData - Datos del servicio logístico
 * @param logisticServiceHistory - Historial del servicio logístico
 * @returns Historial completo
 */
export const buildCompleteLogisticServiceHistory = (
  logisticServiceData: IlogisticServiceDetails,
  logisticServiceHistory: ILogisticServiceHistory[]
): ILogisticServiceHistory[] => {
  const { historySL, statusName } = logisticServiceData;
  if (!historySL || !statusName) return [];

  const baseHistory = createBaseLogisticServiceHistory(historySL, statusName);
  return combineBaseHistoryWithLogisticServiceHistory(statusName, baseHistory, logisticServiceHistory);
};
